import { useCallback, type Dispatch, type SetStateAction } from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { SiteContent } from "../../home/shared/types/siteContent";
import {
  updateChallengeById,
} from "../../challenges/services/challengeAdminService";
import type {
  ChallengeDetail,
  ChallengeType,
} from "../../challenges/types/challengeAdmin";
import type {
  ChallengeEventDraft,
  ChallengeModalMode,
  DraftPair,
  PairMode,
} from "../../challenges/types/challengeDraft";
import {
  normalizeTournamentShirtSizes,
  type TournamentRegistrationCategoryItem,
} from "../utils/tournamentCategories";
import {
  MIN_EVENT_AMOUNT_CENTS,
  normalizeChallengeDetail,
  parseAmountToCents,
  parseRoundsInput,
} from "../utils/adminChallengeEditor";
import type { AdminEventChallengeLink } from "../services/eventRegistrationAdminService";
import { syncSavedChallengeEvent } from "../utils/adminChallengeEventSync";
import {
  buildChallengePersistencePayload,
  validateChallengeScheduleDraft,
} from "../utils/adminChallengePersistence";

type UseAdminChallengePersistenceInput = {
  autoCreateMissingRegistrationsAndCharges: (input: {
    eventId: string;
    challengeType: ChallengeType;
  }) => Promise<void>;
  canStartChallenge: boolean;
  challengeDetail: ChallengeDetail | null;
  challengeDurationDraft: string;
  challengeEventDraft: ChallengeEventDraft;
  challengeIdFromRoute?: number;
  challengeRoundsJson: string;
  challengeScheduledAtDraft: Date | null;
  challengeScheduledEndAtDraft: Date | null;
  challengeSelectedParticipants: string[];
  challengeTournamentCategories: TournamentRegistrationCategoryItem[];
  currentPairMode: PairMode;
  draftPairs: DraftPair[];
  goToAdminListPage: () => void;
  isAdmin: boolean;
  isPoco: boolean;
  loadHistory: () => Promise<void>;
  minimumParticipants: number;
  resolveParticipantCpfsForPersist: (detail: ChallengeDetail) => string[];
  savingChallenge: boolean;
  setChallengeDetail: Dispatch<SetStateAction<ChallengeDetail | null>>;
  setChallengeEventDraft: Dispatch<SetStateAction<ChallengeEventDraft>>;
  setChallengeModalMode: Dispatch<SetStateAction<ChallengeModalMode>>;
  setChallengeRoundsJson: Dispatch<SetStateAction<string>>;
  setContent: Dispatch<SetStateAction<SiteContent>>;
  setEventChallengeLinkDrafts: Dispatch<SetStateAction<Record<string, string>>>;
  setEventChallengeLinks: Dispatch<
    SetStateAction<Record<string, AdminEventChallengeLink>>
  >;
  setSavingChallenge: Dispatch<SetStateAction<boolean>>;
  setSelectedChallengeId: Dispatch<SetStateAction<number | null>>;
  setShowChallengeModal: Dispatch<SetStateAction<boolean>>;
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
  token?: string | null;
};

type SaveChallengeOptions = {
  includeRounds?: boolean;
  roundsOverride?: any[][];
  roundsOnly?: boolean;
};

function parseDateTime(value?: string | null) {
  const date = new Date(String(value || "").trim());
  return Number.isNaN(date.getTime()) ? null : date;
}

function isSameDateTimeInstant(
  firstValue?: string | null,
  secondValue?: string | null,
) {
  const first = parseDateTime(firstValue);
  const second = parseDateTime(secondValue);
  if (!first || !second) return false;
  return first.getTime() === second.getTime();
}

export function useAdminChallengePersistence({
  autoCreateMissingRegistrationsAndCharges,
  canStartChallenge,
  challengeDetail,
  challengeDurationDraft,
  challengeEventDraft,
  challengeIdFromRoute,
  challengeRoundsJson,
  challengeScheduledAtDraft,
  challengeScheduledEndAtDraft,
  challengeSelectedParticipants,
  challengeTournamentCategories,
  currentPairMode,
  draftPairs,
  goToAdminListPage,
  isAdmin,
  isPoco,
  loadHistory,
  minimumParticipants,
  resolveParticipantCpfsForPersist,
  savingChallenge,
  setChallengeDetail,
  setChallengeEventDraft,
  setChallengeModalMode,
  setChallengeRoundsJson,
  setContent,
  setEventChallengeLinkDrafts,
  setEventChallengeLinks,
  setSavingChallenge,
  setSelectedChallengeId,
  setShowChallengeModal,
  showErrorToast,
  showSuccessToast,
  token,
}: UseAdminChallengePersistenceInput) {
  const t = siteTexts.admin;

  const validateCustomChallengeSettings = useCallback(() => {
    if (!challengeDetail) {
      return true;
    }
    const isStandardPoco =
      challengeDetail.challengeType === "poco" &&
      challengeDetail.challengeModel !== "custom";
    if (challengeDetail.challengeModel !== "custom" && !isStandardPoco) {
      return true;
    }
    if (challengeDetail.challengeType !== "super8") {
      const courtsCount = Math.floor(Number(challengeDetail.courtsCount || 0));
      const playersCount =
        challengeDetail.challengeType === "poco" && Number.isFinite(courtsCount)
          ? courtsCount * 4
          : Math.floor(Number(challengeDetail.playersCount || 0));
      if (!Number.isFinite(playersCount) || playersCount < 12) {
        showErrorToast(
          challengeDetail.challengeType === "poco"
            ? t.createChallengePage.toasts.pocoMinPlayersRequired
            : t.createChallengePage.toasts.playersCountRequired,
        );
        return false;
      }
      if (!Number.isFinite(courtsCount) || courtsCount < 2) {
        showErrorToast(t.createChallengePage.toasts.courtsCountRequired);
        return false;
      }
      if (
        challengeDetail.challengeType === "poco" &&
        playersCount !== courtsCount * 4
      ) {
        showErrorToast(t.createChallengePage.toasts.pocoExactPlayersRequired);
        return false;
      }
      if (challengeDetail.challengeType === "challenge") {
        const maxPlayersCount = Math.max(12, courtsCount * 6);
        if (playersCount > maxPlayersCount) {
          showErrorToast(
            t.createChallengePage.toasts.challengeMaxPlayersExceeded,
          );
          return false;
        }
      }
    }
    if (isStandardPoco) return true;
    if (challengeDetail.gameType === "time") {
      const minutes = Math.floor(
        Number(challengeDetail.gameDurationMinutes || 0),
      );
      if (!Number.isFinite(minutes) || minutes <= 0) {
        showErrorToast(t.createChallengePage.toasts.gameDurationRequired);
        return false;
      }
      return true;
    }
    const games = Math.floor(Number(challengeDetail.gameCount || 0));
    if (!Number.isFinite(games) || games <= 0) {
      showErrorToast(t.createChallengePage.toasts.gameCountRequired);
      return false;
    }
    return true;
  }, [
    challengeDetail,
    showErrorToast,
    t.createChallengePage.toasts.courtsCountRequired,
    t.createChallengePage.toasts.gameCountRequired,
    t.createChallengePage.toasts.gameDurationRequired,
    t.createChallengePage.toasts.challengeMaxPlayersExceeded,
    t.createChallengePage.toasts.pocoMinPlayersRequired,
    t.createChallengePage.toasts.pocoExactPlayersRequired,
    t.createChallengePage.toasts.playersCountRequired,
  ]);

  const validateCurrentSchedule = useCallback(
    () =>
      validateChallengeScheduleDraft({
        challengeDurationDraft,
        challengeScheduledAtDraft,
        challengeScheduledEndAtDraft,
        showErrorToast,
      }),
    [
      challengeDurationDraft,
      challengeScheduledAtDraft,
      challengeScheduledEndAtDraft,
      showErrorToast,
    ],
  );

  const validateEventLocation = useCallback(() => {
    if (!challengeDetail) return false;
    if (!String(challengeDetail.city || "").trim()) {
      showErrorToast(t.createChallengePage.toasts.cityRequired);
      return false;
    }
    if (!String(challengeDetail.address || "").trim()) {
      showErrorToast(t.createChallengePage.toasts.clubVenueRequired);
      return false;
    }
    return true;
  }, [
    challengeDetail,
    showErrorToast,
    t.createChallengePage.toasts.cityRequired,
    t.createChallengePage.toasts.clubVenueRequired,
  ]);

  const resolveOrganizerUserId = useCallback(
    (detail: ChallengeDetail, blockWhenMissing: boolean) => {
      const isOfficial = Boolean(detail.isOfficial ?? true);
      const organizerUserId = isOfficial
        ? null
        : (detail.organizerUserId ?? null);
      if (blockWhenMissing && isAdmin && !isOfficial && !organizerUserId) {
        return { ok: true as const, organizerUserId };
      }
      return { ok: true as const, organizerUserId };
    },
    [isAdmin],
  );

  const applyNormalizedChallenge = useCallback(
    (updated: ChallengeDetail) => {
      const normalized = normalizeChallengeDetail(updated);
      setChallengeDetail(normalized);
      setChallengeRoundsJson(JSON.stringify(normalized.rounds, null, 2));
      return normalized;
    },
    [setChallengeDetail, setChallengeRoundsJson],
  );

  const onSaveChallenge = useCallback(async (options?: SaveChallengeOptions) => {
    if (!token || !challengeDetail || savingChallenge) return false;
    const isTournamentEngine =
      String(challengeDetail.challengeType || "")
        .trim()
        .toLowerCase() === "tournament";
    const includeRoundsInPayload = options?.includeRounds !== false;
    const parsedRounds =
      isTournamentEngine || !includeRoundsInPayload
        ? []
        : Array.isArray(options?.roundsOverride)
          ? options.roundsOverride
          : parseRoundsInput(challengeRoundsJson);
    if (!isTournamentEngine && includeRoundsInPayload && !parsedRounds) {
      showErrorToast(t.toasts.challengeRoundsInvalid);
      return false;
    }
    if (options?.roundsOnly) {
      if (isTournamentEngine || !parsedRounds) return false;
      setSavingChallenge(true);
      try {
        const updated = await updateChallengeById(token, challengeDetail.id, {
          rounds: parsedRounds,
        });
        applyNormalizedChallenge(updated);
        showSuccessToast(t.toasts.challengeSaveSuccess);
        await loadHistory();
        return true;
      } catch (err) {
        showErrorToast(
          err instanceof Error && err.message
            ? err.message
            : t.toasts.challengeSaveError,
        );
        return false;
      } finally {
        setSavingChallenge(false);
      }
    }
    const isFinishedChallenge = challengeDetail.status === "finished";
    if (!validateEventLocation()) return false;
    if (!isFinishedChallenge && !validateCurrentSchedule()) return false;
    if (!isFinishedChallenge && !validateCustomChallengeSettings()) {
      return false;
    }
    if (
      !isFinishedChallenge &&
      !String(challengeEventDraft.imageUrl || "").trim()
    ) {
      showErrorToast(t.createChallengePage.toasts.imageRequired);
      return false;
    }
    const amountPerPlayerCents =
      parseAmountToCents(challengeEventDraft.amountPerPlayer || "") ??
      (Number.isFinite(Number(challengeEventDraft.savedAmountPerPlayerCents))
        ? Number(challengeEventDraft.savedAmountPerPlayerCents)
        : undefined);
    if (
      !isFinishedChallenge &&
      (!Number.isFinite(Number(amountPerPlayerCents)) ||
        Number(amountPerPlayerCents) < MIN_EVENT_AMOUNT_CENTS)
    ) {
      showErrorToast(t.createChallengePage.toasts.priceMinimum);
      return false;
    }
    if (
      !isFinishedChallenge &&
      challengeEventDraft.shirtSizesEnabled &&
      normalizeTournamentShirtSizes(challengeEventDraft.shirtSizes).length === 0
    ) {
      showErrorToast(t.createChallengePage.toasts.shirtSizesRequired);
      return false;
    }
    if (
      !isFinishedChallenge &&
      challengeEventDraft.shirtSizesEnabled &&
      challengeEventDraft.shirtAvailabilityType === "period" &&
      !String(challengeEventDraft.shirtDeadlineAt || "").trim()
    ) {
      showErrorToast(t.createChallengePage.toasts.shirtDeadlineRequired);
      return false;
    }
    if (
      !isFinishedChallenge &&
      challengeEventDraft.shirtSizesEnabled &&
      challengeEventDraft.shirtAvailabilityType === "period"
    ) {
      const shirtDeadlineAt = parseDateTime(
        challengeEventDraft.shirtDeadlineAt,
      );
      const shirtDeadlineWasChanged = !isSameDateTimeInstant(
        challengeEventDraft.shirtDeadlineAt,
        challengeEventDraft.savedShirtDeadlineAt,
      );
      const shirtAvailabilityWasChanged =
        Boolean(challengeEventDraft.shirtSizesEnabled) !==
          Boolean(challengeEventDraft.savedShirtSizesEnabled) ||
        String(challengeEventDraft.shirtAvailabilityType || "none") !==
          String(challengeEventDraft.savedShirtAvailabilityType || "none");
      if (
        !shirtDeadlineAt ||
        (shirtDeadlineAt.getTime() < Date.now() &&
          (shirtDeadlineWasChanged || shirtAvailabilityWasChanged))
      ) {
        showErrorToast(t.createChallengePage.toasts.shirtDeadlinePast);
        return false;
      }
    }
    if (
      !isFinishedChallenge &&
      challengeEventDraft.shirtSizesEnabled &&
      challengeEventDraft.shirtAvailabilityType === "quantity" &&
      (!Number.isFinite(Number(challengeEventDraft.shirtQuantityLimit)) ||
        Number(challengeEventDraft.shirtQuantityLimit) <= 0)
    ) {
      showErrorToast(t.createChallengePage.toasts.shirtQuantityRequired);
      return false;
    }

    setSavingChallenge(true);
    try {
      const organizer = resolveOrganizerUserId(
        challengeDetail,
        !isFinishedChallenge,
      );
      if (!organizer.ok) return false;
      const participantCpfs = resolveParticipantCpfsForPersist(challengeDetail);
      const payload = buildChallengePersistencePayload({
        challengeDetail,
        challengeEventDraft,
        draftPairs,
        allowTnpEventIdentity: isAdmin,
        organizerUserId: organizer.organizerUserId,
        participantCpfs,
      });
      const usesRegistrationCategoryParticipants =
        Array.isArray(challengeDetail.registrationCategories) &&
        challengeDetail.registrationCategories.length > 0;
      if (
        isTournamentEngine ||
        usesRegistrationCategoryParticipants ||
        !challengeEventDraft.registrationsOpen
      ) {
        delete payload.participantCpfs;
        delete payload.pairIds;
      }
      if (String(challengeDetail.startedAt || "").trim()) {
        delete payload.participantCpfs;
        delete payload.pairIds;
      }
      const updated = await updateChallengeById(token, challengeDetail.id, {
        ...payload,
        // Não reenvia "status": esta ação só salva os dados do
        // desafio/torneio. Iniciar/finalizar têm botões próprios
        // (onStartChallenge/onFinishChallenge) — reenviar o status atual
        // aqui podia acionar, no backend, a validação de "iniciar" (que
        // exige participantes com pagamento confirmado) mesmo quando o
        // admin só está editando informações gerais.
        finishedAt: challengeDetail.finishedAt,
        ...(!isTournamentEngine && includeRoundsInPayload && parsedRounds
          ? { rounds: parsedRounds }
          : {}),
      });
      const normalized = applyNormalizedChallenge(updated);
      if (!isFinishedChallenge) {
        await syncSavedChallengeEvent({
          autoCreateMissingRegistrationsAndCharges,
          challengeEventDraft,
          challengeTournamentCategories,
          currentPairMode,
          normalized,
          setContent,
          setEventChallengeLinkDrafts,
          setEventChallengeLinks,
          token,
        });
        setChallengeEventDraft((prev) => ({
          ...prev,
          savedShirtSizesEnabled: Boolean(prev.shirtSizesEnabled),
          savedShirtAvailabilityType: prev.shirtAvailabilityType || "none",
          savedShirtDeadlineAt:
            prev.shirtSizesEnabled && prev.shirtAvailabilityType === "period"
              ? prev.shirtDeadlineAt || null
              : null,
        }));
      }
      if (
        normalized.status === "finished" &&
        String(normalized.challengeType || "")
          .trim()
          .toLowerCase() !== "tournament"
      ) {
        setChallengeModalMode("edit");
      }
      showSuccessToast(t.toasts.challengeSaveSuccess);
      await loadHistory();
      return true;
    } catch (err) {
      showErrorToast(
        err instanceof Error && err.message
          ? err.message
          : t.toasts.challengeSaveError,
      );
      return false;
    } finally {
      setSavingChallenge(false);
    }
  }, [
    applyNormalizedChallenge,
    autoCreateMissingRegistrationsAndCharges,
    challengeDetail,
    challengeEventDraft,
    challengeRoundsJson,
    challengeTournamentCategories,
    currentPairMode,
    draftPairs,
    isAdmin,
    loadHistory,
    resolveOrganizerUserId,
    resolveParticipantCpfsForPersist,
    savingChallenge,
    setChallengeEventDraft,
    setChallengeModalMode,
    setContent,
    setEventChallengeLinkDrafts,
    setEventChallengeLinks,
    setSavingChallenge,
    showErrorToast,
    showSuccessToast,
    t.createChallengePage.toasts.priceMinimum,
    t.createChallengePage.toasts.imageRequired,
    t.createChallengePage.toasts.shirtSizesRequired,
    t.createChallengePage.toasts.shirtDeadlineRequired,
    t.createChallengePage.toasts.shirtDeadlinePast,
    t.createChallengePage.toasts.shirtQuantityRequired,
    t.toasts.challengeRoundsInvalid,
    t.toasts.challengeSaveError,
    t.toasts.challengeSaveSuccess,
    token,
    validateCustomChallengeSettings,
    validateCurrentSchedule,
    validateEventLocation,
  ]);

  const onStartChallenge = useCallback(async () => {
    if (!token || !challengeDetail) return;
    if (!canStartChallenge) {
      const participantsCount = challengeSelectedParticipants.length;
      if (participantsCount % 2 !== 0) {
        showErrorToast(t.validation.evenParticipantsRequired);
        return;
      }
      if (challengeDetail.challengeType === "super8") {
        showErrorToast(t.validation.super8ParticipantsRequired);
        return;
      }
      if (isPoco && participantsCount !== minimumParticipants) {
        const requiredPairs = Math.floor(minimumParticipants / 2);
        showErrorToast(
          `No Poço é obrigatório ter exatamente ${minimumParticipants} participantes (${requiredPairs} duplas) para iniciar.`,
        );
        return;
      }
      if (participantsCount < minimumParticipants) {
        showErrorToast(
          `É necessário cadastrar ao menos ${minimumParticipants} participantes para iniciar este desafio.`,
        );
        return;
      }
      showErrorToast(t.validation.cannotStartCurrentConfig);
      return;
    }
    try {
      if (!validateCurrentSchedule()) return;
      if (!validateEventLocation()) return;
      if (!validateCustomChallengeSettings()) return;
      const organizer = resolveOrganizerUserId(challengeDetail, true);
      if (!organizer.ok) return;
      const participantCpfs = resolveParticipantCpfsForPersist(challengeDetail);
      const parsedRounds = parseRoundsInput(challengeRoundsJson);
      if (!parsedRounds) {
        showErrorToast(t.toasts.challengeRoundsInvalid);
        return;
      }
      const payload = buildChallengePersistencePayload({
        challengeDetail,
        challengeEventDraft,
        draftPairs,
        allowTnpEventIdentity: isAdmin,
        organizerUserId: organizer.organizerUserId,
        participantCpfs,
      });
      // As inscrições já são a fonte de verdade neste momento. Reenviar os
      // snapshots locais com as inscrições fechadas faz a API interpretar o
      // início como uma tentativa de alterar participantes.
      delete payload.participantCpfs;
      delete payload.pairIds;
      const updated = await updateChallengeById(token, challengeDetail.id, {
        ...payload,
        rounds: parsedRounds,
        status: "started",
      });
      applyNormalizedChallenge(updated);
      await loadHistory();
      showSuccessToast(t.toasts.challengeStartedSuccess);
    } catch (err) {
      showErrorToast(
        err instanceof Error && err.message
          ? err.message
          : t.toasts.challengeStartError,
      );
    }
  }, [
    applyNormalizedChallenge,
    canStartChallenge,
    challengeDetail,
    challengeEventDraft,
    challengeRoundsJson,
    challengeSelectedParticipants.length,
    draftPairs,
    isAdmin,
    isPoco,
    loadHistory,
    minimumParticipants,
    resolveOrganizerUserId,
    resolveParticipantCpfsForPersist,
    showErrorToast,
    showSuccessToast,
    t.toasts.challengeStartError,
    t.toasts.challengeStartedSuccess,
    t.toasts.challengeRoundsInvalid,
    t.validation.cannotStartCurrentConfig,
    t.validation.evenParticipantsRequired,
    t.validation.super8ParticipantsRequired,
    token,
    validateCustomChallengeSettings,
    validateCurrentSchedule,
    validateEventLocation,
  ]);

  const onFinishChallenge = useCallback(async () => {
    if (!token || !challengeDetail) return;
    try {
      if (!validateCurrentSchedule()) return;
      if (!validateEventLocation()) return;
      if (!validateCustomChallengeSettings()) return;
      const organizer = resolveOrganizerUserId(challengeDetail, true);
      if (!organizer.ok) return;
      const isTournamentToFinish =
        String(challengeDetail.challengeType || "")
          .trim()
          .toLowerCase() === "tournament";
      const participantCpfs = resolveParticipantCpfsForPersist(challengeDetail);
      const updated = await updateChallengeById(token, challengeDetail.id, {
        ...buildChallengePersistencePayload({
          challengeDetail,
          challengeEventDraft,
          draftPairs,
          allowTnpEventIdentity: isAdmin,
          organizerUserId: organizer.organizerUserId,
          participantCpfs,
        }),
        ...(isTournamentToFinish
          ? {}
          : {
              rounds:
                parseRoundsInput(challengeRoundsJson) || challengeDetail.rounds,
            }),
        status: "finished",
      });
      applyNormalizedChallenge(updated);
      setChallengeModalMode("edit");
      await loadHistory();
      showSuccessToast(t.toasts.challengeFinishedSuccess);
    } catch (err) {
      showErrorToast(
        err instanceof Error && err.message
          ? err.message
          : t.toasts.challengeFinishError,
      );
    }
  }, [
    applyNormalizedChallenge,
    challengeDetail,
    challengeEventDraft,
    challengeRoundsJson,
    draftPairs,
    isAdmin,
    loadHistory,
    resolveOrganizerUserId,
    resolveParticipantCpfsForPersist,
    setChallengeModalMode,
    showErrorToast,
    showSuccessToast,
    t.toasts.challengeFinishError,
    t.toasts.challengeFinishedSuccess,
    token,
    validateCustomChallengeSettings,
    validateCurrentSchedule,
    validateEventLocation,
  ]);

  return {
    onFinishChallenge,
    onSaveChallenge,
    onStartChallenge,
  };
}
